import * as React from 'react';

interface MatchResult {
  type: 'email' | 'url' | 'br';
  value: string;
  index: number;
}

interface LinkStyles {
  readonly color: string;
}

interface RegexPatterns {
  readonly EMAIL: RegExp;
  readonly URL: RegExp;
  readonly BR: RegExp;
}

class JsonConverter {
  private static readonly PATTERNS: RegexPatterns = {
    EMAIL: /\b[A-Za-z0-9._%+=-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
    URL: /https:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_.~#?&//=]*)/g,
    BR: /\n/g
  };

  private static readonly STYLES: LinkStyles = {
    color: '#0066cc'
  };

  private static readonly MAILTO_INFO = `Si rien ne s'ouvre, allez dans les paramètres de Windows
1. Sélectionnez « Applications »
2. Sélectionnez « Applications par défaut »
3. Dans la barre de recherche, tapez « MAILTO »
4. Cliquer sur le résultat et selectionnez votre client mail préféré (ex: Outlook, Thunderbird, Mail...)`;

  /**
   * Trouve tous les matches d'un pattern dans un texte
   */
  private static findMatches(text: string, regex: RegExp, type: MatchResult['type']): MatchResult[] {
    const matches: MatchResult[] = [];
    regex.lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      matches.push({
        type,
        value: match[0],
        index: match.index,
      });
    }

    return matches;
  }

  /**
   * Crée un élément React pour un match donné
   */
  private static createReactElement(match: MatchResult, index: number): React.JSX.Element {
    switch (match.type) {
      case 'email':
        return React.createElement('a', {
          key: `email-${index}`,
          href: `mailto:${match.value}`,
          title: this.MAILTO_INFO,
          style: this.STYLES
        }, match.value);

      case 'url':
        return React.createElement('a', {
          key: `url-${index}`,
          href: match.value,
          target: "_blank",
          rel: "noopener noreferrer",
          style: this.STYLES
        }, match.value);

      case 'br':
        return React.createElement('br', { key: `br-${index}` });
    }
  }

  /**
   * Convertit le texte en composants React interactifs
   */
  public static linkifyReact(text: string): (string | React.JSX.Element)[] {
    const matches: MatchResult[] = [
      ...this.findMatches(text, this.PATTERNS.EMAIL, 'email'),
      ...this.findMatches(text, this.PATTERNS.URL, 'url'),
      ...this.findMatches(text, this.PATTERNS.BR, 'br')
    ].sort((a, b) => a.index - b.index);

    return this.buildParts(text, matches);
  }

  /**
   * Construit les parties du résultat final
   */
  private static buildParts(text: string, matches: MatchResult[]): (string | React.JSX.Element)[] {
    const parts: (string | React.JSX.Element)[] = [];
    let lastIndex = 0;

    matches.forEach((match, idx) => {
      if (match.index > lastIndex) {
        const textPart = this.normalizeSpaces(text.substring(lastIndex, match.index));
        parts.push(textPart);
      }

      parts.push(this.createReactElement(match, idx));
      lastIndex = match.index + match.value.length;
    });

    if (lastIndex < text.length) {
      parts.push(this.normalizeSpaces(text.substring(lastIndex)));
    }

    return parts;
  }

  /**
   * Normalise les espaces multiples en un seul espace
   */
  private static normalizeSpaces(text: string): string {
    return text.replace(/\s+/g, ' ');
  }

  /**
   * Retourne un composant React prêt à utiliser
   */
  public static linkifyComponent(text: string): React.ReactNode {
    return this.linkifyReact(text);
  }
}

export default JsonConverter;